import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';

// Set the workerSrc to load from the public directory to avoid CORS and import errors
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

export class DocumentParserService {
    static async parseDocument(file) {
        const buffer = await file.arrayBuffer();

        if (file.name.toLowerCase().endsWith('.pdf')) {
            // No need to load worker dynamically
            return await this.parsePDF(buffer);
        } else if (file.name.toLowerCase().endsWith('.docx')) {
            return await this.parseDOCX(buffer);
        }

        throw new Error('Unsupported file format');
    }

    static async parsePDF(buffer) {
        try {
            const loadingTask = pdfjsLib.getDocument({ data: buffer });
            const pdf = await loadingTask.promise;
            let htmlContent = '';

            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const content = await page.getTextContent();

                // Join text items with newlines to better preserve structure
                const text = content.items
                    .filter((item) => 'str' in item)
                    .map((item) => item.str)
                    .join('\n');

                // Split by lines
                let lines = text.split(/\n+/);
                // Improved merge: If a line is just a bullet, look ahead for the next non-empty line and merge them
                let mergedLines = [];
                for (let i = 0; i < lines.length; i++) {
                    let line = lines[i].trim();
                    // If line is just a bullet symbol
                    if (/^[●•\-*]$/.test(line)) {
                        // Look ahead for the next non-empty line
                        let j = i + 1;
                        while (j < lines.length && lines[j].trim() === '') j++;
                        if (j < lines.length) {
                            mergedLines.push(line + ' ' + lines[j].trim());
                            i = j; // skip to the merged line
                        } else {
                            mergedLines.push(line);
                        }
                    } else {
                        mergedLines.push(line);
                    }
                }
                lines = mergedLines;

                let inList = false;
                let listType = null;
                let paragraphBuffer = [];
                let emptyLineCount = 0;

                function flushParagraph() {
                    if (paragraphBuffer.length > 0) {
                        htmlContent += `<p>${paragraphBuffer.join(' ')}</p>`;
                        paragraphBuffer = [];
                    }
                }

                for (let idx = 0; idx < lines.length; idx++) {
                    const line = lines[idx].trim();
                    const isListItem = /^([●•\-*]|\d+\.)\s+/.test(line);
                    if (!line) {
                        emptyLineCount++;
                        // Only insert <br> for 2+ consecutive empty lines when not in a list
                        if (emptyLineCount === 2 && !inList) {
                            flushParagraph();
                            htmlContent += '<br />';
                        }
                        continue;
                    } else {
                        emptyLineCount = 0;
                    }

                    // List detection (group bullets)
                    if (isListItem) {
                        flushParagraph();
                        const isOrdered = /^\d+\./.test(line);
                        if (!inList || listType !== (isOrdered ? 'ol' : 'ul')) {
                            if (inList) htmlContent += `</${listType}>`;
                            listType = isOrdered ? 'ol' : 'ul';
                            htmlContent += `<${listType}>`;
                            inList = true;
                        }
                        htmlContent += `<li>${line.replace(/^([●•\-*]|\d+\.)\s+/, '')}</li>`;
                        continue;
                    } else if (inList) {
                        htmlContent += `</${listType}>`;
                        inList = false;
                        listType = null;
                    }

                    // Heading detection
                    if (line.length < 100 && !line.endsWith('.') && !/\.\s/.test(line) && !/^\d+\.\s/.test(line)) {
                        flushParagraph();
                        const isAllCaps = line === line.toUpperCase();
                        const isTitleCase = line
                            .split(' ')
                            .every((word) => word.length === 0 || word[0] === word[0].toUpperCase());
                        if (line.length < 30 && isAllCaps) {
                            htmlContent += `<h4>${line}</h4>`;
                            continue;
                        } else if (line.length < 60 && isTitleCase) {
                            htmlContent += `<h4>${line}</h4>`;
                            continue;
                        } else {
                            htmlContent += `<h4>${line}</h4>`;
                            continue;
                        }
                    }

                    // Buffer regular paragraph lines
                    paragraphBuffer.push(line);
                }
                flushParagraph();
                if (inList) htmlContent += `</${listType}>`;
                if (i < pdf.numPages) {
                    htmlContent += '<hr class="page-break" />';
                }
            }

            // Convert all h1, h2, and h3 to h4 for compactness
            htmlContent = htmlContent.replace(/<h1>/g, '<h4>').replace(/<\/h1>/g, '</h4>');
            htmlContent = htmlContent.replace(/<h2>/g, '<h4>').replace(/<\/h2>/g, '</h4>');
            htmlContent = htmlContent.replace(/<h3>/g, '<h4>').replace(/<\/h3>/g, '</h4>');

            // Replace multiple consecutive <br> or <br /> with a single <br />
            htmlContent = htmlContent.replace(/(<br\s*\/?>\s*){2,}/g, '<br />');
            // Remove multiple blank lines (newlines) in HTML, keeping only one
            htmlContent = htmlContent.replace(/(\n\s*){2,}/g, '\n');
            // Remove all newlines that are not inside <pre> or <code> tags
            htmlContent = htmlContent.replace(/(?<!<pre>|<code>)[\n\r]+/g, '');
            // Trim leading/trailing whitespace
            htmlContent = htmlContent.trim();

            return htmlContent;
        } catch (error) {
            throw new Error('Failed to parse PDF file');
        }
    }

    static async parseDOCX(buffer) {
        try {
            // Use simple options that won't cause type errors
            const styleMap = [
                "p[style-name='Heading 1'] => h1:fresh",
                "p[style-name='Heading 2'] => h2:fresh",
                "p[style-name='Heading 3'] => h3:fresh",
                "p[style-name='Title'] => h1:fresh",
                "p[style-name='Subtitle'] => h2:fresh",
            ];

            const result = await mammoth.convertToHtml({
                arrayBuffer: buffer,
                styleMap,
            });

            return result.value;
        } catch (error) {
            throw new Error('Failed to parse DOCX file');
        }
    }

    static processListItems(html) {
        // Process unordered lists
        let processedHtml = html.replace(/<li>([^<]*)<\/li>/g, (match, content) => {
            return `<ul><li>${content}</li></ul>`;
        });

        // Merge adjacent ul elements
        processedHtml = processedHtml.replace(/<\/ul>\s*<ul>/g, '');

        return processedHtml;
    }
}
